import { faUserGroup } from '@fortawesome/free-solid-svg-icons';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useSnapshot } from 'valtio';
import { ServiceListingStatus } from '../../../__generated__/graphql';
import { Button } from '../../components/button';
import { Modal } from '../../components/modal';
import {
  Alert,
  Badge,
  Card,
  CardBody,
  EmptyState,
  Field,
  Input,
  InlineRow,
  LoadingBlock,
  Muted,
  PageContainer,
  PageHeader,
  Select,
  Stack,
  Strong,
} from '../../components/ui';
import { useGetMyLocationsV2LeanQuery } from '../../services/locationServices/__generated__/queries.generated';
import { MY_WORKERS } from '../../services/serviceService/queries';
import {
  useCopyWorkerSettingsMutation,
  useCreateWorkerDraftMutation,
  useMyWorkersQuery,
} from '../../services/serviceService/__generated__/queries.generated';
import { authStore } from '../../../store/authStore';
import { toPublicS3ObjectUrl } from '../../utils/s3PublicUrl';
import { SERVICE_TYPE_LABELS } from './priceTables';
import { ProviderProfileCard } from './ProviderProfileCard';
import { serviceErrorMessage } from './serviceErrors';
import {
  PROVIDER_LOCATIONS_PATH,
  PROVIDER_NEW_WORKER_PATH,
  workerAccountsPath,
  workerEditPath,
  workerManagePath,
  workerPricingPath,
} from './servicePaths';
import {
  isDraftStatus,
  LISTING_STATUS_LABELS,
  onTimeText,
  resumeWizardStep,
  type ProviderWorker,
} from './workerForm';

const Grid = styled.div`
  ${tw`grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3`}
`;

const Cover = styled.div`
  ${tw`relative w-full overflow-hidden`}
  border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  aspect-ratio: 16 / 9;
  background: var(--color-bg-secondary);
  img {
    ${tw`object-cover w-full h-full`}
  }
`;

const CoverBadges = styled.div`
  ${tw`absolute flex gap-2`}
  top: 12px;
  left: 12px;
`;

const Actions = styled.div`
  ${tw`flex flex-wrap gap-2`}
`;

const statusTone = (status: ServiceListingStatus) =>
  status === ServiceListingStatus.Listed
    ? 'success'
    : status === ServiceListingStatus.Unlisted
      ? 'neutral'
      : 'warning';

function CopyWorkerModal({ source, onClose }: { source: ProviderWorker; onClose: () => void }) {
  const navigate = useNavigate();
  const locationsQuery = useGetMyLocationsV2LeanQuery({
    variables: { input: { page: 1, limit: 50 } },
    fetchPolicy: 'cache-and-network',
  });
  const [name, setName] = useState('');
  const [locationId, setLocationId] = useState(String(source.car.location?.id ?? ''));
  const [error, setError] = useState<string | null>(null);
  const [createDraft, { loading: creating }] = useCreateWorkerDraftMutation();
  const [copySettings, { loading: copying }] = useCopyWorkerSettingsMutation({
    refetchQueries: [{ query: MY_WORKERS }],
  });
  const locations = locationsQuery.data?.getMyLocationsV2.items ?? [];

  const onCopy = async () => {
    const displayName = name.trim();
    if (!displayName || displayName.length > 60) {
      setError('Give the new worker a name of 1 to 60 characters');
      return;
    }
    if (!locationId) {
      setError('Pick a location');
      return;
    }
    setError(null);
    try {
      const { data } = await createDraft({
        variables: { locationId: Number(locationId), displayName },
      });
      const created = data?.createWorkerDraft;
      if (!created) return;
      await copySettings({
        variables: { fromWorkerCarId: source.carId, toWorkerCarId: created.carId },
      });
      toast.success(`Settings copied to ${displayName}`);
      navigate(workerEditPath(created.carId, 'profile'));
    } catch (e) {
      setError(serviceErrorMessage(e, 'Could not copy the settings'));
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Copy settings to a new worker">
      <Stack $gap={4}>
        <Strong>Copy {source.worker.displayName}'s settings to a new worker</Strong>
        <Muted $size="sm">
          Prices, area, hours, supplies and equipment photos are copied. A background check is not:
          handovers stay off until the new worker has their own.
        </Muted>
        {error && (
          <Alert tone="error" title="Not copied">
            {error}
          </Alert>
        )}
        <Field label="New worker's name" htmlFor="copy-name" required>
          <Input
            id="copy-name"
            value={name}
            maxLength={60}
            onChange={e => setName(e.target.value)}
            data-testid="copy-worker-name"
          />
        </Field>
        <Field label="Location" htmlFor="copy-location" required>
          <Select
            id="copy-location"
            value={locationId}
            onChange={e => setLocationId(e.target.value)}
          >
            <option value="">Pick a location</option>
            {locations.map(location => (
              <option key={location.id} value={String(location.id)}>
                {location.name} ({location.city} {location.zipCode})
              </option>
            ))}
          </Select>
        </Field>
        <InlineRow>
          <Button
            type="button"
            text="Create and copy"
            onClick={onCopy}
            isLoading={creating || copying}
            dataTestId="copy-worker-submit"
          />
          <Button type="button" theme="text" text="Cancel" onClick={onClose} />
        </InlineRow>
      </Stack>
    </Modal>
  );
}

function WorkerCard({ worker, onCopy }: { worker: ProviderWorker; onCopy: () => void }) {
  const navigate = useNavigate();
  const status = worker.settings.listingStatus;
  const draft = isDraftStatus(status);
  const cover = worker.worker.equipmentPhotos[0];
  const services = worker.worker.offerings
    .filter(o => o.isActive)
    .map(o => SERVICE_TYPE_LABELS[o.serviceType]);
  return (
    <Card data-testid={`worker-card-${worker.carId}`}>
      <Cover>
        {cover && <img src={toPublicS3ObjectUrl(cover)} alt="" loading="lazy" />}
        <CoverBadges>
          <Badge tone={statusTone(status)} dot>
            {LISTING_STATUS_LABELS[status]}
          </Badge>
          {worker.worker.paused && (
            <Badge
              tone="error"
              title={worker.settings.pauseReason ?? undefined}
              data-testid="worker-paused"
            >
              Paused
            </Badge>
          )}
        </CoverBadges>
      </Cover>
      <CardBody>
        <Stack $gap={4}>
          <div>
            <Strong>{worker.worker.displayName}</Strong>
            <div>
              <Muted $size="sm">
                {worker.car.location
                  ? `${worker.car.location.city} ${worker.car.location.zipCode}`
                  : ''}
              </Muted>
            </div>
            <div>
              <Muted $size="sm">{services.length ? services.join(', ') : 'No services yet'}</Muted>
            </div>
            <div>
              <Muted $size="sm" data-testid="worker-on-time">
                {onTimeText(worker.worker.onTimeRate, worker.worker.completedJobCount)}
              </Muted>
            </div>
          </div>
          {worker.worker.paused && worker.settings.pauseReason && (
            <Alert tone="error" title="Paused">
              {worker.settings.pauseReason}
            </Alert>
          )}
          <Actions>
            <Button
              type="button"
              size="sm"
              text={draft ? 'Finish setup' : 'Manage'}
              onClick={() =>
                navigate(
                  draft
                    ? workerEditPath(worker.carId, resumeWizardStep(worker.settings.wizardStep))
                    : workerManagePath(worker.carId),
                )
              }
            />
            <Button
              type="button"
              theme="secondary"
              size="sm"
              text="Prices"
              onClick={() => navigate(workerPricingPath(worker.carId))}
            />
            <Button
              type="button"
              theme="secondary"
              size="sm"
              text="Logins"
              onClick={() => navigate(workerAccountsPath(worker.carId))}
            />
            <Button
              type="button"
              theme="text"
              size="sm"
              text="Copy to a new worker"
              onClick={onCopy}
              dataTestId={`worker-copy-${worker.carId}`}
            />
          </Actions>
        </Stack>
      </CardBody>
    </Card>
  );
}

/** The Provider's workers (spec 019 W7). */
export default function WorkersPage() {
  const navigate = useNavigate();
  const { user } = useSnapshot(authStore);
  const query = useMyWorkersQuery({ fetchPolicy: 'cache-and-network' });
  const [copyFrom, setCopyFrom] = useState<ProviderWorker | null>(null);
  const workers = query.data?.myWorkers ?? [];

  return (
    <PageContainer width="lg" canvas data-testid="workers-page">
      <PageHeader
        eyebrow="Provider"
        title="Workers"
        description="Each worker is booked like a car: one job at a time, with their own prices and area."
        actions={
          <Button
            type="button"
            text="Add a worker"
            onClick={() => navigate(PROVIDER_NEW_WORKER_PATH)}
            dataTestId="workers-add"
          />
        }
      />
      <Stack $gap={6}>
        {user && !user.isHost && (
          <Alert tone="info" title="Apply to be a Provider">
            You can set up workers now. Customers see them once your application is approved.{' '}
            <Link to="/profile">Apply</Link>
          </Alert>
        )}
        <ProviderProfileCard />
        {query.loading && !query.data ? (
          <LoadingBlock label="Loading your workers" />
        ) : query.error && !query.data ? (
          <Alert tone="error" title="Could not load your workers">
            {serviceErrorMessage(query.error)}
          </Alert>
        ) : workers.length === 0 ? (
          <Card>
            <EmptyState
              icon={faUserGroup}
              title="No workers yet"
              description={
                <>
                  Add a <Link to={PROVIDER_LOCATIONS_PATH}>location</Link> first, then a worker who
                  works from it.
                </>
              }
              action={
                <Button
                  type="button"
                  text="Add a worker"
                  onClick={() => navigate(PROVIDER_NEW_WORKER_PATH)}
                />
              }
            />
          </Card>
        ) : (
          <Grid>
            {workers.map(worker => (
              <WorkerCard key={worker.carId} worker={worker} onCopy={() => setCopyFrom(worker)} />
            ))}
          </Grid>
        )}
      </Stack>
      {copyFrom && <CopyWorkerModal source={copyFrom} onClose={() => setCopyFrom(null)} />}
    </PageContainer>
  );
}
